import { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Popover,
    Radio,
    RadioGroup,
    Select,
    Skeleton,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShareIcon from '@mui/icons-material/Share';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import axiosInstance from '../../helpers/axios';
import { useInstituteOptions } from '../FibeCommissionBilling/hooks/useInstituteOptions';

// Same "L3 Manager" grouping AssignRoleModal.js already uses for this codebase's roles.
const TEAM_MANAGER_ROLES = ['collection_manager', 'crm'];

// Keeps long option lists from overflowing the viewport and getting shifted above the select.
const SELECT_MENU_PROPS = {
    variant: 'menu',
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
    PaperProps: { style: { maxHeight: 250 } },
};

function TeamTableSkeleton() {
    return (
        <TableBody>
            {Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                    {Array.from({ length: 8 }).map((_, j) => (
                        <TableCell key={j}><Skeleton variant="text" /></TableCell>
                    ))}
                </TableRow>
            ))}
        </TableBody>
    );
}

export default function TeamManagementTab() {
    const [teams, setTeams] = useState([]);
    const [zones, setZones] = useState([]);
    const [managerOptions, setManagerOptions] = useState([]);
    const [zoneFilter, setZoneFilter] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const { institutes, isLoading: institutesLoading } = useInstituteOptions();

    const [modalOpen, setModalOpen] = useState(false);
    const [editingTeam, setEditingTeam] = useState(null);
    const [name, setName] = useState('');
    const [zoneId, setZoneId] = useState('');
    const [managerUserId, setManagerUserId] = useState('');
    const [teamType, setTeamType] = useState('dedicated');
    const [department, setDepartment] = useState('processing');
    const [assignedInstitutes, setAssignedInstitutes] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [modalError, setModalError] = useState(null);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteError, setDeleteError] = useState(null);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [instituteAnchorEl, setInstituteAnchorEl] = useState(null);
    const [instituteList, setInstituteList] = useState([]);

    const loadTeams = useCallback(() => {
        setIsLoading(true);
        setErrorMessage(null);
        axiosInstance
            .get('/admin-settings/teams', { params: zoneFilter ? { zoneId: zoneFilter } : undefined })
            .then((response) => setTeams(response.data.data))
            .catch(() => setErrorMessage('Could not load teams.'))
            .finally(() => setIsLoading(false));
    }, [zoneFilter]);

    useEffect(() => {
        loadTeams();
    }, [loadTeams]);

    useEffect(() => {
        axiosInstance.get('/admin-settings/zones').then((response) => setZones(response.data.data)).catch(() => setZones([]));
        axiosInstance
            .get('/admin-settings/users', { params: { role: TEAM_MANAGER_ROLES.join(','), limit: 100 } })
            .then((response) => setManagerOptions(response.data.data.users))
            .catch(() => setManagerOptions([]));
    }, []);

    const zoneNameById = new Map(zones.map((z) => [z.id, z.name]));
    const managerNameById = new Map(managerOptions.map((u) => [u.id, u.name]));
    const instituteNameById = new Map(institutes.map((i) => [i.id, i.brandName || i.id]));

    const totalTeams = teams.length;
    const dedicatedCount = teams.filter((t) => t.teamType === 'dedicated').length;
    const sharedCount = teams.filter((t) => t.teamType === 'shared').length;

    const openCreateModal = () => {
        setEditingTeam(null);
        setName('');
        setZoneId('');
        setManagerUserId('');
        setTeamType('dedicated');
        setDepartment('processing');
        setAssignedInstitutes([]);
        setModalError(null);
        setModalOpen(true);
    };

    const openEditModal = (team) => {
        setEditingTeam(team);
        setName(team.name || '');
        setZoneId(team.zoneId || '');
        setManagerUserId(team.managerUserId || '');
        setTeamType(team.teamType || 'dedicated');
        setDepartment(team.department || 'processing');
        setAssignedInstitutes(team.assignedInstitutes || []);
        setModalError(null);
        setModalOpen(true);
    };

    const handleSave = () => {
        setIsSaving(true);
        setModalError(null);
        const payload = {
            name,
            zoneId,
            managerUserId: managerUserId || undefined,
            teamType,
            department,
            assignedInstitutes: teamType === 'dedicated' ? assignedInstitutes : undefined,
        };
        const request = editingTeam
            ? axiosInstance.put(`/admin-settings/teams/${editingTeam.id}`, payload)
            : axiosInstance.post('/admin-settings/teams', payload);

        request
            .then(() => {
                setModalOpen(false);
                setSnackbar({
                    open: true,
                    message: editingTeam ? 'Team updated successfully' : 'Team created successfully',
                    severity: 'success',
                });
                loadTeams();
            })
            .catch((error) => {
                setModalError(error?.response?.data?.message || 'Could not save team.');
            })
            .finally(() => setIsSaving(false));
    };

    const handleDelete = () => {
        if (!deleteTarget) return;
        setDeleteError(null);
        axiosInstance
            .delete(`/admin-settings/teams/${deleteTarget.id}`)
            .then(() => {
                setDeleteTarget(null);
                setSnackbar({ open: true, message: 'Team deleted successfully', severity: 'success' });
                loadTeams();
            })
            .catch((error) => {
                if (error?.response?.status === 409) {
                    setDeleteError('Cannot delete: team has assigned users. Reassign them first.');
                } else {
                    setDeleteError(error?.response?.data?.message || 'Could not delete team.');
                }
            });
    };

    const canSave = name.trim().length > 0 && Boolean(zoneId);

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 20px', borderRadius: '14px',
                    border: '1.5px solid #2563eb33', boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <GroupsIcon sx={{ fontSize: 22, color: '#2563eb' }} />
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">Total Teams</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>{totalTeams}</Typography>
                    </Box>
                </Box>
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 20px', borderRadius: '14px',
                    border: '1.5px solid #16a34a33', boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <CheckCircleIcon sx={{ fontSize: 22, color: '#16a34a' }} />
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">Dedicated</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>{dedicatedCount}</Typography>
                    </Box>
                </Box>
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 20px', borderRadius: '14px',
                    border: '1.5px solid #0d948833', boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: '#f0fdfa', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <ShareIcon sx={{ fontSize: 22, color: '#0d9488' }} />
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">Shared</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>{sharedCount}</Typography>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <Select
                    size="small"
                    displayEmpty
                    value={zoneFilter}
                    onChange={(e) => setZoneFilter(e.target.value)}
                    sx={{ minWidth: 200 }}
                >
                    <MenuItem value="">All Zones</MenuItem>
                    {zones.map((z) => <MenuItem key={z.id} value={z.id}>{z.name}</MenuItem>)}
                </Select>
                <Button variant="contained" onClick={openCreateModal} sx={{ color: '#fff' }}>Create Team</Button>
            </Box>

            {errorMessage && <Alert severity="error" sx={{ marginBottom: '12px' }}>{errorMessage}</Alert>}

            <Paper variant="outlined" sx={{ borderRadius: '8px' }}>
                <TableContainer>
                    <Table>
                        <TableHead sx={{ backgroundColor: '#F8F8F8' }}>
                            <TableRow>
                                <TableCell>Team Name</TableCell>
                                <TableCell>Zone</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Department</TableCell>
                                <TableCell>Manager</TableCell>
                                <TableCell>Assigned Institutes</TableCell>
                                <TableCell>Members</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        {isLoading ? (
                            <TeamTableSkeleton />
                        ) : teams.length === 0 ? (
                            <TableBody>
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ color: 'text.secondary', padding: '32px' }}>
                                        No teams created yet. Create your first team to start organizing work.
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        ) : (
                            <TableBody>
                                {teams.map((team) => {
                                    const teamInstitutes = team.assignedInstitutes || [];
                                    return (
                                        <TableRow key={team.id} hover>
                                            <TableCell sx={{ fontWeight: 500 }}>{team.name}</TableCell>
                                            <TableCell>{zoneNameById.get(team.zoneId) || '—'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={team.teamType === 'dedicated' ? 'Dedicated' : 'Shared'}
                                                    sx={{
                                                        backgroundColor: team.teamType === 'dedicated' ? 'success.light' : 'info.light',
                                                        color: team.teamType === 'dedicated' ? 'success.dark' : 'info.dark',
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ textTransform: 'capitalize' }}>{team.department}</TableCell>
                                            <TableCell>{team.managerUserId ? (managerNameById.get(team.managerUserId) || team.managerUserId) : '—'}</TableCell>
                                            <TableCell>
                                                {teamInstitutes.length === 0 ? '—' : institutesLoading ? (
                                                    <Skeleton variant="text" width={100} />
                                                ) : (
                                                    <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                                        {teamInstitutes.slice(0, 2).map((instId) => (
                                                            <Chip key={instId} size="small" label={instituteNameById.get(instId) || instId} />
                                                        ))}
                                                        {teamInstitutes.length > 2 && (
                                                            <Chip
                                                                size="small"
                                                                variant="outlined"
                                                                label={`+${teamInstitutes.length - 2} more`}
                                                                onClick={(e) => {
                                                                    setInstituteAnchorEl(e.currentTarget);
                                                                    setInstituteList(teamInstitutes.map((instId) => instituteNameById.get(instId) || instId));
                                                                }}
                                                                sx={{ cursor: 'pointer' }}
                                                            />
                                                        )}
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell>{team.memberCount || 0}</TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" onClick={() => openEditModal(team)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => { setDeleteTarget(team); setDeleteError(null); }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        )}
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                fullWidth
                maxWidth="sm"
                sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
                PaperProps={{ sx: { marginTop: '32px' } }}
            >
                <DialogTitle>{editingTeam ? 'Edit Team' : 'Create Team'}</DialogTitle>
                <DialogContent>
                    {modalError && <Alert severity="error" sx={{ marginBottom: '16px' }}>{modalError}</Alert>}
                    <TextField
                        fullWidth
                        label="Name"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        sx={{ marginBottom: '16px', marginTop: '8px' }}
                    />
                    <FormControl fullWidth sx={{ marginBottom: '16px' }}>
                        <InputLabel shrink>Zone</InputLabel>
                        <Select
                            displayEmpty
                            label="Zone"
                            value={zoneId}
                            onChange={(e) => {
                                const newZoneId = e.target.value;
                                setZoneId(newZoneId);
                                setAssignedInstitutes((prev) =>
                                    prev.filter((instId) => institutes.find((i) => i.id === instId)?.zoneId === newZoneId),
                                );
                            }}
                            MenuProps={SELECT_MENU_PROPS}
                        >
                            <MenuItem value="" disabled>Select zone</MenuItem>
                            {zones.map((z) => <MenuItem key={z.id} value={z.id}>{z.name}</MenuItem>)}
                        </Select>
                    </FormControl>

                    <Box sx={{ marginBottom: '16px' }}>
                        <Typography variant="body2" sx={{ marginBottom: '4px' }}>Type</Typography>
                        <RadioGroup row value={teamType} onChange={(e) => setTeamType(e.target.value)}>
                            <FormControlLabel
                                value="dedicated"
                                control={<Radio />}
                                label={
                                    <Tooltip
                                        title="This team owns a fixed list of Assigned Institutes (must belong to its zone). Members inherit exactly this list."
                                        placement="top"
                                        arrow
                                        slotProps={{ tooltip: { sx: { maxWidth: 240 } } }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            Dedicated <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                        </Box>
                                    </Tooltip>
                                }
                            />
                            <FormControlLabel
                                value="shared"
                                control={<Radio />}
                                label={
                                    <Tooltip
                                        title="This team has no fixed institute list — each member's institute access must be assigned individually."
                                        placement="top"
                                        arrow
                                        slotProps={{ tooltip: { sx: { maxWidth: 240 } } }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            Shared <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                        </Box>
                                    </Tooltip>
                                }
                            />
                        </RadioGroup>
                    </Box>

                    <Box sx={{ marginBottom: '16px' }}>
                        <Typography variant="body2" sx={{ marginBottom: '4px' }}>Department</Typography>
                        <RadioGroup row value={department} onChange={(e) => setDepartment(e.target.value)}>
                            <FormControlLabel value="processing" control={<Radio />} label="Processing" />
                            <FormControlLabel value="collection" control={<Radio />} label="Collection" />
                            <FormControlLabel value="both" control={<Radio />} label="Both" />
                        </RadioGroup>
                    </Box>

                    <FormControl fullWidth sx={{ marginBottom: '16px' }}>
                        <InputLabel shrink>Team Head</InputLabel>
                        <Select
                            displayEmpty
                            label="Team Head"
                            value={managerUserId}
                            onChange={(e) => setManagerUserId(e.target.value)}
                            MenuProps={SELECT_MENU_PROPS}
                        >
                            <MenuItem value="">No manager</MenuItem>
                            {managerOptions.map((u) => (
                                <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {teamType === 'dedicated' && (
                        <Autocomplete
                            multiple
                            options={institutes.filter((i) => i.zoneId === zoneId)}
                            getOptionLabel={(option) => option.brandName || option.id}
                            value={institutes.filter((i) => assignedInstitutes.includes(i.id))}
                            onChange={(e, value) => setAssignedInstitutes(value.map((v) => v.id))}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip label={option.brandName} size="small" {...getTagProps({ index })} />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField {...params} label="Assigned Institutes" helperText={!zoneId ? 'Select a zone first' : `Only institutes in the selected zone can be assigned`} />
                            )}
                            disabled={!zoneId}
                            ListboxProps={{ style: { maxHeight: 250 } }}
                            slotProps={{
                                popper: {
                                    placement: 'bottom-start',
                                    modifiers: [{ name: 'flip', enabled: false }],
                                },
                            }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setModalOpen(false)} disabled={isSaving}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={!canSave || isSaving} sx={{ color: '#fff' }}>
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
                <DialogTitle>Delete Team</DialogTitle>
                <DialogContent>
                    {deleteError && <Alert severity="error" sx={{ marginBottom: '16px' }}>{deleteError}</Alert>}
                    Are you sure you want to delete team {deleteTarget?.name}?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
                </DialogActions>
            </Dialog>

            <Popover
                open={Boolean(instituteAnchorEl)}
                anchorEl={instituteAnchorEl}
                onClose={() => setInstituteAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Box sx={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: 280 }}>
                    {instituteList.map((name) => (
                        <Chip key={name} size="small" label={name} sx={{ justifyContent: 'flex-start' }} />
                    ))}
                </Box>
            </Popover>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
